import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SubPageLayout from "@/components/SubPageLayout-v2";
import PageMeta from "@/components/PageMeta";
import {
    CheckCircle2, XCircle, Target, LineChart, Users, Rocket,
    Building, Building2, ArrowRight, BookOpen,
} from "lucide-react";

function FadeIn({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
    const ref = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setVisible(true); },
            { threshold: 0.12 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);
    return (
        <div ref={ref}
            style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(28px)",
                transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms` }}>
            {children}
        </div>
    );
}

const C = {
    bg: "#F3F3F5",
    bgCard: "#FFFFFF",
    text: "#1A1A2E",
    textMuted: "#5A5A72",
    textLight: "#8A8AA0",
    accent: "#2563EB",
    accentDark: "#1D4ED8",
    purple: "#7C3AED",
    green: "#059669",
    amber: "#D97706",
    border: "rgba(26,26,46,0.08)",
    shadow: "0 1px 3px rgba(26,26,46,0.06), 0 8px 32px rgba(26,26,46,0.04)",
    shadowHover: "0 8px 40px rgba(37,99,235,0.10), 0 2px 8px rgba(26,26,46,0.06)",
};

const tiers = [
    {
        size: "PMI & Startup",
        icon: "rocket",
        color: C.amber,
        tagline: "AI Readiness Assessment",
        items: [
            "Valutazione maturità AI e tool selection",
            "Quick-win individuati in 30 giorni",
            "Formazione pratica per team fino a 20 persone",
            "ROI misurabile dal primo trimestre",
        ],
    },
    {
        size: "Medie Imprese",
        icon: "zap",
        color: C.accent,
        tagline: "AI Strategy & Implementation",
        items: [
            "Roadmap AI allineata al business",
            "Implementazione use case prioritari",
            "Change management per 25-100 persone",
            "KPI di adozione e produttività",
        ],
    },
    {
        size: "Enterprise",
        icon: "landmark",
        color: C.purple,
        tagline: "Enterprise AI Transformation",
        items: [
            "Ecosystem AI multi-paese e multi-funzione",
            "Governance, compliance AI Act, risk management",
            "Learning path per ruolo e seniority",
            "Executive coaching e leadership alignment",
        ],
    },
];

const faqs = [
    {
        q: "Quanto costa un progetto di AI consulting?",
        a: "Dipende dalla scala e dagli obiettivi. Un AI Readiness Assessment per una PMI parte da poche migliaia di euro; un progetto di Enterprise AI Transformation richiede un investimento più strutturato. La prima call di 30 minuti è gratuita e serve proprio a definire scope, tempi e budget realistici.",
    },
    {
        q: "Quanto dura un engagement tipico?",
        a: "I progetti di AI consulting che seguo vanno da 6 settimane (assessment + quick-win) a 12-18 mesi (trasformazione enterprise). Il differenziatore è che non ti lascio con un piano in mano: ti accompagno fino ai risultati misurabili.",
    },
    {
        q: "Lavori solo con enterprise o anche con PMI?",
        a: "Entrambe. Ho pacchetti calibrati sulla scala: PMI e startup, medie imprese e enterprise. Ogni dimensione ha esigenze diverse in termini di budget, governance e velocità di adozione.",
    },
    {
        q: "Come misurate il ROI dell'AI?",
        a: "Definisco KPI dal giorno uno: adozione (quante persone usano i tool), produttività (tempo risparmiato per task), qualità (error rate, output quality) e impatto economico (costi evitati, revenue generata). Niente slide da consulente: numeri concreti.",
    },
    {
        q: "Che differenza c'è tra AI consulting e AI enablement?",
        a: "Il consulting tradizionale produce strategia e framework. L'AI enablement aggiunge formazione hands-on, change management e accompagnamento fino all'adozione reale. Io faccio enablement: strategia + formazione + risultati, non solo slide.",
    },
    {
        q: "Serve avere già dati o infrastruttura AI?",
        a: "No. Parte dell'assessment iniziale è proprio capire a che punto sei: dati disponibili, tool già in uso, competenze del team. Da lì costruiamo un piano realistico — che spesso parte da tool AI commerciali già pronti, non da sviluppo custom.",
    },
];

export default function AiConsultingServicesPage() {
    return (
        <SubPageLayout>
            <PageMeta
                title="AI Consulting Services Italia — Strategia & ROI"
                description="Servizi di AI consulting per PMI ed enterprise: strategia, implementazione, formazione. 18 anni di esperienza NVIDIA. ROI misurabile dal primo trimestre."
                path="/ai-consulting-services"
            />

            {/* JSON-LD: Service + FAQPage */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
                "@context": "https://schema.org",
                "@graph": [
                    {
                        "@type": "Service",
                        "@id": "https://www.ballerano.com/ai-consulting-services#service",
                        "name": "AI Consulting Services",
                        "serviceType": "AI Consulting",
                        "description": "Servizi di AI consulting e AI enablement per PMI, medie imprese ed enterprise: strategia, implementazione, formazione hands-on e change management.",
                        "provider": {
                            "@type": "Person",
                            "name": "Luciano Ballerano",
                            "jobTitle": "AI Enabler & Transformation Consultant",
                            "url": "https://www.ballerano.com",
                        },
                        "areaServed": { "@type": "Country", "name": "Italy" },
                        "url": "https://www.ballerano.com/ai-consulting-services",
                    },
                    {
                        "@type": "FAQPage",
                        "mainEntity": faqs.map(f => ({
                            "@type": "Question",
                            "name": f.q,
                            "acceptedAnswer": { "@type": "Answer", "text": f.a },
                        })),
                    },
                    {
                        "@type": "BreadcrumbList",
                        "itemListElement": [
                            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.ballerano.com/" },
                            { "@type": "ListItem", "position": 2, "name": "AI Consulting Services", "item": "https://www.ballerano.com/ai-consulting-services" },
                        ],
                    },
                ],
            }) }} />

            {/* ═══════ HERO ═══════ */}
            <section style={{ padding: "clamp(70px, 10vw, 110px) 24px 60px", background: C.bg }}>
                <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
                    <FadeIn>
                        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: C.accent, marginBottom: 14, display: "block" }}>
                            AI Consulting Services
                        </span>
                        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.1rem, 5vw, 3.2rem)",
                            fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 18, color: C.text, lineHeight: 1.15 }}>
                            AI Consulting per aziende<br />che vogliono risultati reali
                        </h1>
                        <p style={{ fontSize: 17, lineHeight: 1.6, color: C.textMuted, maxWidth: 680, margin: "0 auto 28px" }}>
                            Il 70% dei progetti AI fallisce. Non per la tecnologia, ma per come viene introdotta.
                            Offro servizi di AI consulting che coprono strategia, implementazione e adozione —
                            con 18 anni di esperienza NVIDIA e un approccio vendor-neutral.
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* ═══════ COS'È L'AI CONSULTING ═══════ */}
            <section style={{ padding: "clamp(60px, 10vw, 100px) 24px", background: C.bgCard, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
                <div style={{ maxWidth: 820, margin: "0 auto" }}>
                    <FadeIn>
                        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.7rem, 3.5vw, 2.4rem)",
                            fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 20, color: C.text }}>
                            Cos'è l'AI consulting (e perché da solo non basta)
                        </h2>
                        <p style={{ fontSize: 16, lineHeight: 1.75, color: C.textMuted, marginBottom: 16 }}>
                            L'AI consulting tradizionale risponde a domande come: <em>quali use case di machine learning
                            ha senso attivare?</em> Quale piattaforma di generative AI scegliere? Come integrare gli
                            LLM e le pipeline RAG nei processi aziendali? Come governare l'AI Act e la compliance?
                        </p>
                        <p style={{ fontSize: 16, lineHeight: 1.75, color: C.textMuted, marginBottom: 16 }}>
                            Sono domande giuste. Ma la risposta — sotto forma di roadmap e framework — è solo il
                            punto di partenza. Il vero problema arriva dopo: <strong style={{ color: C.text }}>
                            chi traduce la strategia in adozione reale?</strong> Chi forma i team, gestisce il
                            change management, rende l'AI parte del lavoro quotidiano?
                        </p>
                        <p style={{ fontSize: 16, lineHeight: 1.75, color: C.textMuted }}>
                            Ed è qui che la maggior parte dei progetti si ferma. Io lavoro da entrambi i lati:
                            AI consulting per definire la direzione, AI enablement per farla succedere davvero.
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* ═══════ CONSULTING vs ENABLEMENT ═══════ */}
            <section style={{ padding: "clamp(60px, 10vw, 100px) 24px", background: C.bg }}>
                <div style={{ maxWidth: 1000, margin: "0 auto" }}>
                    <FadeIn>
                        <div style={{ textAlign: "center", marginBottom: 40 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: C.accent, marginBottom: 12, display: "block" }}>
                                Il differenziatore
                            </span>
                            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.7rem, 3.3vw, 2.3rem)",
                                fontWeight: 700, letterSpacing: "-0.02em", color: C.text }}>
                                AI Consulting vs AI Enablement
                            </h2>
                        </div>
                    </FadeIn>

                    <FadeIn delay={100}>
                        <div className="compare-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                            <div style={{ padding: 28, borderRadius: 14, background: C.bgCard, border: `1px solid ${C.border}` }}>
                                <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 18, color: C.textMuted }}>AI Consulting tradizionale</h3>
                                {[
                                    "Strategia e roadmap su slide",
                                    "Framework di governance",
                                    "Vendor assessment e tool selection",
                                    "Si ferma al piano",
                                    "I team devono arrangiarsi sull'adozione",
                                ].map((item, i) => (
                                    <div key={i} style={{ display: "flex", gap: 10, marginBottom: 12, alignItems: "flex-start" }}>
                                        <XCircle size={16} style={{ color: "#DC2626", flexShrink: 0, marginTop: 3 }} />
                                        <span style={{ fontSize: 14, color: C.textMuted, lineHeight: 1.55 }}>{item}</span>
                                    </div>
                                ))}
                            </div>

                            <div style={{ padding: 28, borderRadius: 14, background: C.accent, color: "white", border: `1px solid ${C.accent}`,
                                boxShadow: "0 8px 32px rgba(37,99,235,0.2)" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
                                    <img src="/logo-lb3.png" alt="" loading="lazy" decoding="async" width={22} height={22} style={{ width: 22, height: 22, borderRadius: 6 }} />
                                    <h3 style={{ fontSize: 16, fontWeight: 700 }}>AI Consulting + Enablement</h3>
                                </div>
                                {[
                                    "Strategia concreta e action plan",
                                    "Governance + implementazione pratica",
                                    "Vendor-neutral, calibrato su bisogni reali",
                                    "Formazione hands-on per i tuoi team",
                                    "Change management fino al ROI",
                                ].map((item, i) => (
                                    <div key={i} style={{ display: "flex", gap: 10, marginBottom: 12, alignItems: "flex-start" }}>
                                        <CheckCircle2 size={16} style={{ color: "rgba(255,255,255,0.95)", flexShrink: 0, marginTop: 3 }} />
                                        <span style={{ fontSize: 14, lineHeight: 1.55, color: "rgba(255,255,255,0.95)" }}>{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </FadeIn>

                    <FadeIn delay={200}>
                        <div style={{ textAlign: "center", marginTop: 28 }}>
                            <Link to="/metodologia" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: C.accent, fontWeight: 600, fontSize: 14.5, textDecoration: "none" }}>
                                Scopri la metodologia completa <ArrowRight size={14} />
                            </Link>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* ═══════ SERVIZI PER DIMENSIONE ═══════ */}
            <section style={{ padding: "clamp(60px, 10vw, 100px) 24px", background: C.bgCard, borderTop: `1px solid ${C.border}` }}>
                <div style={{ maxWidth: 1000, margin: "0 auto", textAlign: "center" }}>
                    <FadeIn>
                        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: C.accent, marginBottom: 12, display: "block" }}>
                            I servizi
                        </span>
                        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.7rem, 3.3vw, 2.3rem)",
                            fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 10, color: C.text }}>
                            Servizi di AI consulting per ogni dimensione
                        </h2>
                        <p style={{ fontSize: 16, color: C.textMuted, maxWidth: 560, margin: "0 auto 36px" }}>
                            Pacchetti calibrati sulla maturità digitale e la scala della tua organizzazione
                        </p>
                    </FadeIn>

                    <div className="grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, marginBottom: 36 }}>
                        {tiers.map((tier, i) => (
                            <FadeIn key={i} delay={i * 100}>
                                <div className="card-hover" style={{
                                    padding: "28px 24px", borderRadius: 14, background: C.bg, border: `1px solid ${C.border}`,
                                    boxShadow: C.shadow, textAlign: "left", height: "100%",
                                }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                                        {tier.icon === "rocket" && <Rocket size={22} color={tier.color} />}
                                        {tier.icon === "zap" && <Building size={22} color={tier.color} />}
                                        {tier.icon === "landmark" && <Building2 size={22} color={tier.color} />}
                                        <h3 style={{ fontSize: 17, fontWeight: 700, fontFamily: "'Playfair Display', serif", color: C.text }}>{tier.size}</h3>
                                    </div>
                                    <p style={{ fontSize: 13, fontWeight: 600, color: tier.color, marginBottom: 14 }}>{tier.tagline}</p>
                                    <ul style={{ listStyle: "none", padding: 0 }}>
                                        {tier.items.map((item, j) => (
                                            <li key={j} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "flex-start" }}>
                                                <CheckCircle2 size={15} style={{ color: tier.color, flexShrink: 0, marginTop: 2 }} />
                                                <span style={{ fontSize: 14, color: C.textMuted, lineHeight: 1.5 }}>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </FadeIn>
                        ))}
                    </div>

                    <FadeIn delay={200}>
                        <Link to="/servizi" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: C.accent, fontWeight: 600, fontSize: 14.5, textDecoration: "none" }}>
                            Dettaglio completo dei servizi <ArrowRight size={14} />
                        </Link>
                    </FadeIn>
                </div>
            </section>

            {/* ═══════ METODOLOGIA ═══════ */}
            <section style={{ padding: "clamp(60px, 10vw, 100px) 24px", background: C.bg, borderTop: `1px solid ${C.border}` }}>
                <div style={{ maxWidth: 900, margin: "0 auto" }}>
                    <FadeIn>
                        <div style={{ textAlign: "center", marginBottom: 44 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: C.green, marginBottom: 12, display: "block" }}>
                                Metodologia
                            </span>
                            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.7rem, 3.3vw, 2.3rem)",
                                fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 10, color: C.text }}>
                                Dall'assessment al ROI misurabile
                            </h2>
                            <p style={{ fontSize: 16, color: C.textMuted, maxWidth: 560, margin: "0 auto" }}>
                                Un processo in 4 fasi, pensato per portarti dal "ne sentiamo parlare" al "lo usiamo ogni giorno"
                            </p>
                        </div>
                    </FadeIn>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 18 }}>
                        {[
                            { icon: <Target size={22} />, step: "01", title: "Assessment", desc: "Maturità AI, dati, tool, competenze. Quick-win individuati in 30 giorni." },
                            { icon: <BookOpen size={22} />, step: "02", title: "Strategia", desc: "Roadmap concreta con use case prioritari, KPI e timeline realistici." },
                            { icon: <Users size={22} />, step: "03", title: "Enablement", desc: "Formazione hands-on, change management, community interna di practice." },
                            { icon: <LineChart size={22} />, step: "04", title: "Adozione & ROI", desc: "Misurazione continua di adozione, produttività e impatto economico." },
                        ].map((step, i) => (
                            <FadeIn key={i} delay={i * 80}>
                                <div className="card-hover" style={{
                                    padding: "26px 22px", borderRadius: 14, background: C.bgCard, border: `1px solid ${C.border}`,
                                    boxShadow: C.shadow, height: "100%",
                                }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                                        <div style={{ width: 38, height: 38, borderRadius: 10, background: `${C.accent}12`,
                                            display: "flex", alignItems: "center", justifyContent: "center", color: C.accent }}>
                                            {step.icon}
                                        </div>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: C.textLight, letterSpacing: "0.06em" }}>{step.step}</span>
                                    </div>
                                    <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: C.text, fontFamily: "'Playfair Display', serif" }}>
                                        {step.title}
                                    </h3>
                                    <p style={{ fontSize: 14, lineHeight: 1.6, color: C.textMuted }}>{step.desc}</p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════ PERCHÉ ME ═══════ */}
            <section style={{ padding: "clamp(60px, 10vw, 100px) 24px", background: C.bgCard, borderTop: `1px solid ${C.border}` }}>
                <div style={{ maxWidth: 900, margin: "0 auto" }}>
                    <FadeIn>
                        <div style={{ textAlign: "center", marginBottom: 40 }}>
                            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.7rem, 3.3vw, 2.3rem)",
                                fontWeight: 700, letterSpacing: "-0.02em", color: C.text }}>
                                Perché scegliere me come AI consultant
                            </h2>
                        </div>
                    </FadeIn>

                    <div className="grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 }}>
                        {[
                            { title: "18 anni in NVIDIA", desc: "Non teoria da libro: esperienza reale nel cuore dell'industria AI, su progetti enterprise globali." },
                            { title: "Vendor-neutral", desc: "Non vendo licenze. Scelgo i tool giusti per il tuo contesto, senza lock-in o commissioni nascoste." },
                            { title: "Dalla strategia al ROI", desc: "Non ti lascio con un piano: ti accompagno fino ai risultati misurabili sul campo." },
                        ].map((item, i) => (
                            <FadeIn key={i} delay={i * 100}>
                                <div style={{
                                    padding: 26, borderRadius: 14, background: C.bg, border: `1px solid ${C.border}`,
                                    boxShadow: C.shadow, height: "100%",
                                }}>
                                    <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10, color: C.text, fontFamily: "'Playfair Display', serif" }}>
                                        {item.title}
                                    </h3>
                                    <p style={{ fontSize: 14, lineHeight: 1.65, color: C.textMuted }}>{item.desc}</p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>

                    <FadeIn delay={300}>
                        <div style={{ textAlign: "center", marginTop: 32 }}>
                            <Link to="/chi-sono" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: C.accent, fontWeight: 600, fontSize: 14.5, textDecoration: "none" }}>
                                Chi sono <ArrowRight size={14} />
                            </Link>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* ═══════ FAQ ═══════ */}
            <section style={{ padding: "clamp(60px, 10vw, 100px) 24px", background: C.bg, borderTop: `1px solid ${C.border}` }}>
                <div style={{ maxWidth: 820, margin: "0 auto" }}>
                    <FadeIn>
                        <div style={{ textAlign: "center", marginBottom: 40 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: C.accent, marginBottom: 12, display: "block" }}>
                                FAQ
                            </span>
                            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.7rem, 3.3vw, 2.3rem)",
                                fontWeight: 700, letterSpacing: "-0.02em", color: C.text }}>
                                Domande frequenti sull'AI consulting
                            </h2>
                        </div>
                    </FadeIn>

                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                        {faqs.map((faq, i) => (
                            <FadeIn key={i} delay={i * 50}>
                                <details style={{
                                    padding: "18px 22px", borderRadius: 12, background: C.bgCard, border: `1px solid ${C.border}`,
                                    boxShadow: C.shadow,
                                }}>
                                    <summary style={{ fontSize: 15.5, fontWeight: 700, color: C.text, cursor: "pointer", fontFamily: "'Playfair Display', serif" }}>
                                        {faq.q}
                                    </summary>
                                    <p style={{ fontSize: 14.5, lineHeight: 1.7, color: C.textMuted, marginTop: 12, marginBottom: 0 }}>
                                        {faq.a}
                                    </p>
                                </details>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════ CTA FINALE ═══════ */}
            <section style={{ padding: "clamp(60px, 10vw, 100px) 24px", background: C.bgCard, borderTop: `1px solid ${C.border}` }}>
                <div style={{ maxWidth: 700, margin: "0 auto", textAlign: "center" }}>
                    <FadeIn>
                        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.6rem, 3vw, 2.1rem)",
                            fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 14, color: C.text }}>
                            Partiamo dalla tua situazione reale
                        </h2>
                        <p style={{ fontSize: 16, color: C.textMuted, marginBottom: 28, lineHeight: 1.6 }}>
                            Trovi tutti i dettagli del metodo e dei percorsi in questa pagina.
                        </p>
                    </FadeIn>
                </div>
            </section>
        </SubPageLayout>
    );
}
